import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Plus, Inbox, MessageSquare, ChevronRight } from 'lucide-react';
import { ChatFolder, assignChatToFolder, reorderFolders } from '@/services/folderService';
import { Chat } from '@/services/chatService';
import { FolderItem } from './FolderItem';
import { DraggableChatItem } from './DraggableChatItem';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface FolderListProps {
  folders: ChatFolder[];
  chats: Chat[];
  plan: string;
  selectedFolderId?: string | null;
  onCreateFolder: () => void;
  onEditFolder: (folder: ChatFolder) => void;
  onDeleteFolder: (folder: ChatFolder) => void;
  onEditChat: (chat: Chat) => void;
  onDeleteChat: (chat: Chat) => void;
  onTogglePin?: (chat: Chat) => void;
  onRefresh: () => void;
}

export function FolderList({
  folders,
  chats,
  plan,
  selectedFolderId,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onEditChat,
  onDeleteChat,
  onTogglePin,
  onRefresh,
}: FolderListProps) {
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [uncategorizedOpen, setUncategorizedOpen] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group chats by folder
  const chatsByFolder = useMemo(() => {
    const grouped: Record<string, Chat[]> = { uncategorized: [] };
    folders.forEach((folder) => {
      grouped[folder.id] = [];
    });
    chats.forEach((chat) => {
      if (chat.folder_id && grouped[chat.folder_id]) {
        grouped[chat.folder_id].push(chat);
      } else {
        grouped.uncategorized.push(chat);
      }
    });
    return grouped;
  }, [folders, chats]);

  const uncategorizedChats = chatsByFolder.uncategorized || [];

  // Droppable for uncategorized section
  const { setNodeRef: setUncategorizedRef, isOver: isOverUncategorized } = useDroppable({
    id: 'droppable-uncategorized',
    data: {
      type: 'folder',
      folderId: null,
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over?.id as string | null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle chat being dropped on a folder
    if (activeData?.type === 'chat') {
      const chat = activeData.chat as Chat;
      let targetFolderId: string | null = null;

      // Check if dropped on a folder
      if (overData?.type === 'folder') {
        targetFolderId = overData.folderId as string | null;
      } 
      // Check if dropped on another chat (get its folder)
      else if (overData?.type === 'chat') {
        const targetChat = overData.chat as Chat;
        targetFolderId = targetChat.folder_id;
      }

      if (chat.folder_id !== targetFolderId) {
        try {
          await assignChatToFolder(chat.id, targetFolderId);
          const targetFolder = folders.find(f => f.id === targetFolderId);
          toast({
            title: 'Conversación movida',
            description: targetFolderId 
              ? `Movida a "${targetFolder?.name || 'carpeta'}"` 
              : 'Movida a "Sin categoría"',
          });
          onRefresh();
        } catch (error) {
          console.error('Error moving chat:', error);
          toast({
            title: 'Error',
            description: 'No se pudo mover la conversación',
            variant: 'destructive',
          });
        }
      }
      return;
    }

    // Handle folder reordering
    if (activeData?.type === 'folder' && overData?.type === 'folder') {
      const activeFolder = activeData.folder as ChatFolder;
      const overFolder = overData.folder as ChatFolder;

      if (activeFolder.id !== overFolder?.id) {
        const oldIndex = folders.findIndex((f) => f.id === activeFolder.id);
        const newIndex = folders.findIndex((f) => f.id === overFolder?.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = [...folders];
          const [removed] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, removed);

          try {
            await reorderFolders(
              activeFolder.user_id,
              newOrder.map((f) => f.id)
            );
            onRefresh();
          } catch (error) {
            console.error('Error reordering folders:', error);
          }
        }
      }
    }
  };

  // Handle moving chat to folder via menu
  const handleMoveToFolder = async (chat: Chat, folderId: string | null) => {
    if (chat.folder_id === folderId) return;
    
    try {
      await assignChatToFolder(chat.id, folderId);
      const targetFolder = folders.find(f => f.id === folderId);
      toast({
        title: 'Conversación movida',
        description: folderId 
          ? `Movida a "${targetFolder?.name || 'carpeta'}"` 
          : 'Movida a "Sin categoría"',
      });
      onRefresh();
    } catch (error) {
      console.error('Error moving chat:', error);
      toast({
        title: 'Error',
        description: 'No se pudo mover la conversación',
        variant: 'destructive',
      });
    }
  };

  // Find active chat for drag overlay
  const activeChat = activeId
    ? chats.find((c) => c.id === activeId)
    : null;

  const folderIds = folders.map((f) => `folder-${f.id}`);

  // Custom collision detection that prioritizes droppable folders
  const customCollisionDetection = (args: any) => {
    // First check for droppable intersections (folders)
    const droppableCollisions = rectIntersection(args);
    
    // Filter to prioritize folder droppables
    const folderCollisions = droppableCollisions.filter(
      (collision: any) => collision.id?.toString().startsWith('droppable-')
    );
    
    if (folderCollisions.length > 0) {
      return folderCollisions;
    }
    
    // Fall back to closest center for other elements
    return closestCenter(args);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-1">
        {/* Uncategorized section - now collapsible */}
        <div
          ref={setUncategorizedRef}
          className={`rounded-lg transition-colors ${
            isOverUncategorized ? 'bg-primary/10 ring-2 ring-primary/30' : ''
          }`}
        >
          <button
            onClick={() => setUncategorizedOpen(!uncategorizedOpen)}
            className="flex items-center gap-2 px-3 py-1.5 w-full hover:bg-muted/50 rounded-lg transition-colors"
          >
            <ChevronRight 
              className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
                uncategorizedOpen ? 'rotate-90' : ''
              }`} 
            />
            <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground flex-1 text-left">
              Sin categoría
            </span>
            <span className="text-[10px] text-muted-foreground">
              ({uncategorizedChats.length})
            </span>
          </button>
          {uncategorizedOpen && uncategorizedChats.length > 0 && (
            <div className="ml-3 pl-2 border-l border-border/50 space-y-0.5 py-1">
              <SortableContext
                items={uncategorizedChats.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {uncategorizedChats.map((chat) => (
                  <DraggableChatItem
                    key={chat.id}
                    chat={chat}
                    plan={plan}
                    folders={folders}
                    onEditChat={onEditChat}
                    onDeleteChat={onDeleteChat}
                    onMoveToFolder={handleMoveToFolder}
                    onTogglePin={onTogglePin}
                  />
                ))}
              </SortableContext>
            </div>
          )}
        </div>

        {/* Folders */}
        <SortableContext items={folderIds} strategy={verticalListSortingStrategy}>
          {folders.map((folder) => (
            <FolderItem
              key={folder.id}
              folder={folder}
              chats={chatsByFolder[folder.id] || []}
              plan={plan}
              allFolders={folders}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
              onEditChat={onEditChat}
              onDeleteChat={onDeleteChat}
              onMoveToFolder={handleMoveToFolder}
              onTogglePin={onTogglePin}
              isOver={overId === `droppable-${folder.id}`}
            />
          ))}
        </SortableContext>

        {/* New folder button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onCreateFolder}
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground h-8 px-3"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="text-xs">Nueva carpeta</span>
        </Button>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeChat ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-card border rounded-lg shadow-lg">
            <MessageSquare className="h-3.5 w-3.5 text-secondary" />
            <span className="text-xs font-medium truncate">
              {activeChat.title || '(Sin título)'}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
